/**
 * accessibility-manager.ts
 *
 * Centralized accessibility coordinator implementing WCAG 2.2 AA standards:
 * 1. Focus Restoration Anchor: Captures active element prior to modal opening and restores focus
 *    on close (with fallback to `#main-content` if unmounted).
 * 2. Focus Trap for Dialogs & Non-Modal Landmark Navigation for Drawers.
 * 3. Selective Screen-Reader Live Region Queue (polite vs assertive announcements).
 * 4. Behavioral Reduced Motion state management.
 */

export interface ScreenReaderAnnouncement {
  readonly id: string;
  readonly message: string;
  readonly priority: 'polite' | 'assertive';
  readonly timestamp: number;
}

export type FocusRestorationTarget = HTMLElement | string | null;

export class AccessibilityManager {
  private static instance: AccessibilityManager | null = null;
  private previousActiveElement: HTMLElement | null = null;
  private announcements: ScreenReaderAnnouncement[] = [];
  private listeners: Set<(announcements: readonly ScreenReaderAnnouncement[]) => void> = new Set();
  private prefersReducedMotion: boolean = false;
  private motionListeners: Set<(reduced: boolean) => void> = new Set();

  private constructor() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.prefersReducedMotion = mediaQuery.matches;
      mediaQuery.addEventListener('change', (e) => {
        this.prefersReducedMotion = e.matches;
        this.notifyMotionListeners();
      });
    }
  }

  public static getInstance(): AccessibilityManager {
    if (!AccessibilityManager.instance) {
      AccessibilityManager.instance = new AccessibilityManager();
    }
    return AccessibilityManager.instance;
  }

  /**
   * Captures the currently active element before opening a modal dialog.
   */
  public captureActiveFocus(): void {
    if (typeof document !== 'undefined') {
      this.previousActiveElement = document.activeElement as HTMLElement | null;
    }
  }

  /**
   * Restores focus to the previously captured active element.
   * If the element was unmounted or is unavailable, falls back to `#main-content` or document.body.
   */
  public restoreActiveFocus(fallbackSelector: string = '#main-content'): void {
    if (typeof document === 'undefined') return;

    if (this.previousActiveElement && document.contains(this.previousActiveElement)) {
      this.previousActiveElement.focus();
    } else {
      const fallback = document.querySelector<HTMLElement>(fallbackSelector) || document.body;
      if (fallback) {
        if (!fallback.hasAttribute('tabindex') && fallback !== document.body) {
          fallback.setAttribute('tabindex', '-1');
        }
        fallback.focus();
      }
    }
    this.previousActiveElement = null;
  }

  /**
   * Dispatches a screen reader announcement via a live region queue.
   */
  public announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!message || message.trim().length === 0) return;
    const item: ScreenReaderAnnouncement = {
      id: `a11y-announcement-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      message: message.trim(),
      priority,
      timestamp: Date.now(),
    };
    this.announcements = [...this.announcements.slice(-10), item];
    this.notifyListeners();
  }

  public subscribeAnnouncements(listener: (announcements: readonly ScreenReaderAnnouncement[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.announcements);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const list = Object.freeze([...this.announcements]);
    this.listeners.forEach((l) => l(list));
  }

  public isReducedMotion(): boolean {
    return this.prefersReducedMotion;
  }

  public subscribeReducedMotion(listener: (reduced: boolean) => void): () => void {
    this.motionListeners.add(listener);
    listener(this.prefersReducedMotion);
    return () => {
      this.motionListeners.delete(listener);
    };
  }

  private notifyMotionListeners(): void {
    this.motionListeners.forEach((l) => l(this.prefersReducedMotion));
  }

  /**
   * Helper to trap focus within a container element during modal dialog interactions.
   */
  public trapFocus(container: HTMLElement, event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;

    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const focusableElements = Array.from(container.querySelectorAll<HTMLElement>(focusableSelectors)).filter(
      (el) => el.offsetParent !== null || el.offsetWidth > 0 || el.offsetHeight > 0
    );

    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }
}

export const globalAccessibilityManager = AccessibilityManager.getInstance();
